import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authClient } from "../utils/amplifyClient";
import { getUrl, uploadData, remove as removeFromStorage } from "aws-amplify/storage";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/browser";
import {
    getCompany as getCompanyQuery,
    listCompanyOwners as listCompanyOwnersQuery,
    usersByCompanyId,
    listSingleUnits,
    listBillingRateByCompanyId,
    listCompanyNoteByCompanyIdAndCreatedAt,
} from "../graphql/queries";
import { Company, CompanyOwner, BillingRate, CompanyNote } from "../types/schema";
import { isDateInFuture, willExpireWithinAWeek, formatDate } from "../utils/helperFunctions";
import { useUserContext } from "../contexts/UserContext";
import { ArchiveModal } from "./site-admin/ArchiveModal";
import { EditCompanyProfileModal } from "./site-admin/EditCompanyProfileModal";
import { ManageSubscriptionModal } from "./site-admin/ManageSubscriptionModal";
import { EditBillingRatesModal } from "./site-admin/EditBillingRatesModal";
import { AddNoteModal } from "./site-admin/AddNoteModal";
import { BackButton, Btn } from "./ui";
import { updateCompany as updateCompanyMutation, updateCompanyOwner, createBillingRate as createBillingRateMutation, updateBillingRate as updateBillingRateMutation, createCompanyNote as createCompanyNoteMutation } from "../graphql/mutations";
import { fetchPMConfigurationsByCompany } from "../graphql/pmQueries";
import { countPmFormsForBilling } from "../utils/pmBillingUtils";
import "../css/CustomerProfilePage.css";



interface UsageCounts {
    users: number; equipment: number; pm: number; tires: number;
    costTracking: number; training: number; skillTracking: number;
}

const MODULES = [
    { name: "Inspections", field: "initialStarterPackageEnrollment" },
    { name: "Equipment", field: "_always" },
    { name: "Preventive Maintenance", field: "pmModuleEnrollment" },
    { name: "Tire Maintenance", field: "tiresModuleEnrollment" },
    { name: "Cost Tracking", field: "costTrackingModuleEnrollment" },
    { name: "Safety / Training", field: "trainingModuleEnrollment" },
    { name: "Reports", field: "_always" },
    { name: "User Skillsets", field: "skillTrackingModuleEnrollment" },
];

export const CustomerProfilePage = ({ readOnly = false }: { readOnly?: boolean }) => {
    const { companyId: urlCompanyId } = useParams<{ companyId: string }>();
    const navigate = useNavigate();
    const {
        companyOwner: currentAdmin,
        company: userCompany,
        companyUser,
        businessAdmin,
        isLoading: isUserLoading,
        setCompany: setUserContextCompany,
    } = useUserContext();

    // Use URL param if available, otherwise fall back to the logged-in user's company
    const companyId = urlCompanyId || userCompany?.id || currentAdmin?.companyId || companyUser?.companyId;

    const [company, setCompany] = useState<Company | null>(null);
    const [profileSettled, setProfileSettled] = useState(false);
    const [owner, setOwner] = useState<CompanyOwner | null>(null);
    const [logoUrl, setLogoUrl] = useState("");
    const [usage, setUsage] = useState<UsageCounts>({ users: 0, equipment: 0, pm: 0, tires: 0, costTracking: 0, training: 0, skillTracking: 0 });
    const [billingRate, setBillingRate] = useState<BillingRate | null>(null);
    const [notes, setNotes] = useState<CompanyNote[]>([]);

    // Modal state
    const [isArchiveOpen, setIsArchiveOpen] = useState(false);
    const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
    const [isManageSubOpen, setIsManageSubOpen] = useState(false);
    const [isEditBillingOpen, setIsEditBillingOpen] = useState(false);
    const [isAddNoteOpen, setIsAddNoteOpen] = useState(false);

    useEffect(() => {
        if (!urlCompanyId && isUserLoading) return;
        if (!companyId) {
            setCompany(null);
            setProfileSettled(true);
            return;
        }
        let cancelled = false;
        setProfileSettled(false);
        (async () => {
            await fetchAll();
            if (!cancelled) setProfileSettled(true);
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, isUserLoading, urlCompanyId]);

    const fetchAll = async () => {
        try {
            const [companyRes, ownersRes] = await Promise.all([
                authClient.graphql({ query: getCompanyQuery, variables: { id: companyId! } }),
                authClient.graphql({ query: listCompanyOwnersQuery }),
            ]);
            const companyData = companyRes.data.getCompany as Company;
            setCompany(companyData);
            if (companyData?.logo) loadLogo(companyData.logo);

            const matchedOwner = ownersRes.data.listCompanyOwners.items.find((o) => o.companyId === companyId);
            if (matchedOwner) setOwner(matchedOwner as CompanyOwner);

            // Usage counts — PM bills by configured PM forms (units with a PM configuration), not enrollment toggles.
            const [usersRes, unitsRes, pmConfigs] = await Promise.all([
                authClient.graphql({ query: usersByCompanyId, variables: { companyId: companyId! } }),
                authClient.graphql({ query: listSingleUnits }),
                fetchPMConfigurationsByCompany(companyId!),
            ]);
            const users = usersRes.data.usersByCompanyId.items;
            // Gen2 SingleUnit has no `category` relation (only categoryId); preserve prior
            // runtime semantics where `u.category` resolves to undefined.
            const units = unitsRes.data.listSingleUnits.items.filter(
                (u) => (u as { category?: { companyId?: string | null } | null }).category?.companyId === companyId,
            );
            setUsage({
                users: users.length, equipment: units.length,
                pm: countPmFormsForBilling(pmConfigs || []),
                tires: units.filter((u) => !!u.tiresModuleEnrollment).length,
                costTracking: units.filter((u) => !!u.costTrackingModuleEnrollment).length,
                training: users.filter((u) => !!u.trainingModuleEnrollment).length,
                skillTracking: users.filter((u) => !!u.skillTrackingModuleEnrollment).length,
            });

            // Billing rates
            try {
                const billingRes = await authClient.graphql({ query: listBillingRateByCompanyId, variables: { companyId: companyId! } });
                const rates = billingRes.data.listBillingRateByCompanyId.items.filter(Boolean);
                if (rates.length > 0) setBillingRate(rates[0] as BillingRate);
            } catch (e) { console.error("Error fetching billing rates:", e); }

            // Notes
            try {
                const notesRes = await authClient.graphql({
                    query: listCompanyNoteByCompanyIdAndCreatedAt,
                    variables: { companyId: companyId!, sortDirection: "DESC" as any },
                });
                setNotes(notesRes.data.listCompanyNoteByCompanyIdAndCreatedAt.items.filter(Boolean) as CompanyNote[]);
            } catch (e) { console.error("Error fetching notes:", e); }
        } catch (err) {
            console.error("Error fetching customer profile:", err);
            Sentry.captureException(err);
        }
    };

    const loadLogo = async (path: string) => {
        try {
            if (path.startsWith("public/")) { const r = await getUrl({ path }); setLogoUrl(r.url.href); }
            else setLogoUrl(path);
        } catch (err) { console.error("Error loading logo:", err); }
    };

    const getInitials = (name?: string | null) => name ? name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) : "?";

    const formatSubBadge = (type?: string | null) => {
        switch (type) {
            case "paid-full-access": return { label: "Full Access", cls: "full" };
            case "paid-limited-access": return { label: "Limited Access", cls: "limited" };
            case "free-demo": return { label: "Free Demo", cls: "demo" };
            case "paused": return { label: "Paused", cls: "paused" };
            default: return { label: type || "—", cls: "demo" };
        }
    };

    const getStatusInfo = () => {
        if (company?.archived) return { label: "Archived", cls: "archived" };
        if (company?.status === "archived") return { label: "Archived", cls: "archived" };
        if (company?.subscriptionType === "paused") return { label: "Paused", cls: "paused" };
        return { label: "Active", cls: "active" };
    };

    const getExpirationText = () => {
        const exp = company?.subscriptionExpiration;
        if (!exp) return { text: "No expiration", cls: "expired" };
        const today = formatDate(new Date());
        if (!isDateInFuture(exp)) return { text: `Expired: ${exp}`, cls: "expired" };
        if (willExpireWithinAWeek(exp, today)) return { text: `Expiring soon: ${exp}`, cls: "warning" };
        return { text: exp, cls: "active" };
    };

    // Billing computation
    const computeBilling = () => {
        if (!billingRate) return { rows: [], total: 0 };
        const r = billingRate;
        const rows = [
            { module: "Users", active: true, count: usage.users, rate: r.ratePerUser || 0 },
            { module: "Equipment", active: true, count: usage.equipment, rate: r.ratePerEquipment || 0 },
            { module: "PM Forms", active: !!(company as any)?.pmModuleEnrollment, count: usage.pm, rate: r.ratePerPM || 0 },
            { module: "Tire Maintenance", active: !!(company as any)?.tiresModuleEnrollment, count: usage.tires, rate: r.ratePerTireMaint || 0 },
            { module: "Cost Tracking", active: !!(company as any)?.costTrackingModuleEnrollment, count: usage.costTracking, rate: r.ratePerCostTracking || 0 },
            { module: "Safety Enrollment", active: !!(company as any)?.trainingModuleEnrollment, count: usage.training, rate: r.ratePerSafetyEnrollment || 0 },
            { module: "Skill Qualification", active: !!(company as any)?.skillTrackingModuleEnrollment, count: usage.skillTracking, rate: r.ratePerSkillQualification || 0 },
        ];
        const total = rows.reduce((sum, row) => sum + (row.active ? row.count * row.rate : 0), 0);
        return { rows, total };
    };

    if (!company) {
        const waiting = !profileSettled || (!urlCompanyId && isUserLoading);
        if (waiting) {
            return <div className="page-container cpp-container"><p>Loading company profile...</p></div>;
        }
        return (
            <div className="page-container cpp-container">
                <p>
                    {companyId
                        ? 'This company profile could not be loaded.'
                        : businessAdmin
                            ? 'This account is not attached to a company. Open a company from Site Admin.'
                            : 'No company profile is available for this account.'}
                </p>
                {businessAdmin && !companyId && (
                    <Btn variant="primary" buttonText="Site Admin" buttonOnClick={() => navigate('/site-admin')} />
                )}
            </div>
        );
    }

    const sub = formatSubBadge(company.subscriptionType);
    const status = getStatusInfo();
    const expText = getExpirationText();
    const billing = computeBilling();

    // ─── Modal Handlers ──────────────────────────────────
    const handleArchive = async () => {
        try {
            await authClient.graphql({ query: updateCompanyMutation, variables: { input: { id: company!.id, archived: true, status: "archived", subscriptionType: "paused" } } });
            navigate("/site-admin");
        } catch (err) { console.error("Error archiving:", err); Sentry.captureException(err); }
    };

    const handleSaveProfile = async (data: Partial<Company>, logoFile?: File | null) => {
        try {
            const { id: _id, ...rest } = data as any;

            // Handle logo upload/replacement to S3
            let logoPath = company!.logo || "";
            if (logoFile) {
                const ownerId = company!.companyOwnerId || company!.id;
                const fileName = `${uuidv4()}-LOGO-${logoFile.name}`;
                const newPath = `public/companyLogo/${ownerId}/${fileName}`;
                try {
                    await uploadData({ path: newPath, data: logoFile }).result;
                    // Remove the old logo from S3 if it was an uploaded file
                    if (logoPath && logoPath.startsWith("public/")) {
                        try { await removeFromStorage({ path: logoPath }); } catch { /* ignore cleanup errors */ }
                    }
                    logoPath = newPath;
                } catch (uploadErr) {
                    console.error("Error uploading logo:", uploadErr);
                    Sentry.captureException(uploadErr);
                }
            }

            const input = { id: company!.id, name: rest.name, accountNumber: rest.accountNumber, businessType: rest.businessType, primaryLocation: rest.primaryLocation, safetyComplianceStandard: rest.safetyComplianceStandard, assignedRep: rest.assignedRep, industryNAICS: rest.industryNAICS, industrySIC: rest.industrySIC, primaryContactName: rest.primaryContactName, primaryContactPosition: rest.primaryContactPosition, primaryContactEmail: rest.primaryContactEmail, primaryContactPhone: rest.primaryContactPhone, secondaryContactName: rest.secondaryContactName, secondaryContactPosition: rest.secondaryContactPosition, secondaryContactEmail: rest.secondaryContactEmail, secondaryContactPhone: rest.secondaryContactPhone, logo: logoPath };
            const res = await authClient.graphql({ query: updateCompanyMutation, variables: { input } });
            setCompany(res.data.updateCompany as Company);
            // Reflect the updated logo on-page immediately
            if (logoPath && logoPath.startsWith("public/")) {
                try { const r = await getUrl({ path: logoPath }); setLogoUrl(r.url.href); } catch { /* ignore */ }
            } else {
                setLogoUrl(logoPath || "");
            }
            // Keep the left nav / homepage in sync when editing own company
            if (userCompany && userCompany.id === company!.id) {
                setUserContextCompany(res.data.updateCompany as Company);
            }
            setIsEditProfileOpen(false);
        } catch (err) { console.error("Error saving profile:", err); Sentry.captureException(err); }
    };

    const handleSaveSubscription = async (data: { expirationDate: string; subscriptionType: string; modules: Record<string, boolean> }) => {
        try {
            const fmtDate = (d: string) => { const dt = new Date(d); return `${String(dt.getUTCMonth() + 1).padStart(2, "0")}/${String(dt.getUTCDate()).padStart(2, "0")}/${dt.getUTCFullYear()}`; };
            const companyInput: any = { id: company!.id, subscriptionExpiration: fmtDate(data.expirationDate), subscriptionType: data.subscriptionType, ...data.modules };
            const res = await authClient.graphql({ query: updateCompanyMutation, variables: { input: companyInput } });
            setCompany(res.data.updateCompany as Company);
            // If the updated company is the logged-in user's company, update UserContext
            // so the left nav reacts immediately without a page refresh
            if (userCompany && userCompany.id === company!.id) {
                setUserContextCompany(res.data.updateCompany as Company);
            }
            if (owner) {
                await authClient.graphql({ query: updateCompanyOwner, variables: { input: { id: owner.id, subscriptionType: data.subscriptionType } } });
                setOwner({ ...owner, subscriptionType: data.subscriptionType });
            }
            setIsManageSubOpen(false);
        } catch (err) { console.error("Error saving subscription:", err); Sentry.captureException(err); }
    };

    const handleSaveBillingRates = async (rates: Partial<BillingRate>) => {
        try {
            if (billingRate?.id) {
                const res = await authClient.graphql({ query: updateBillingRateMutation, variables: { input: { id: billingRate.id, ...rates } } });
                setBillingRate(res.data.updateBillingRate as BillingRate);
            } else {
                const res = await authClient.graphql({ query: createBillingRateMutation, variables: { input: { companyId: companyId!, ...rates } } });
                setBillingRate(res.data.createBillingRate as BillingRate);
            }
            setIsEditBillingOpen(false);
        } catch (err) { console.error("Error saving billing rates:", err); Sentry.captureException(err); }
    };

    const handleAddNote = async (content: string) => {
        try {
            const authorName = companyUser?.name || currentAdmin?.name || "Unknown";
            const res = await authClient.graphql({ query: createCompanyNoteMutation, variables: { input: { companyId: companyId!, authorName, content } } });
            setNotes((prev) => [res.data.createCompanyNote as CompanyNote, ...prev]);
            setIsAddNoteOpen(false);
        } catch (err) { console.error("Error adding note:", err); Sentry.captureException(err); }
    };

    return (
        <div className="page-container cpp-container">
            {!readOnly && (
                <BackButton to="/site-admin" buttonText="Back to All Companies" />
            )}

            {/* Header Card */}
            <div className="cpp-header-card">
                <div className="cpp-logo-wrapper">
                    {logoUrl ? <img src={logoUrl} alt={company.name} className="cpp-logo" />
                        : <div className="cpp-logo-placeholder">{getInitials(company.name)}</div>}
                </div>
                <div className="cpp-header-info">
                    <h2 className="cpp-company-name">{company.name}</h2>
                    <div className="cpp-header-subtitle">
                        <span className={`cpp-status-dot ${status.cls}`} /> {status.label}
                        {company.accountNumber && <> · Acct #{company.accountNumber}</>}
                        <> · <span className={`cpp-sub-badge ${sub.cls}`}>{sub.label}</span></>
                        {company.initialContactDate && <> · Since {company.initialContactDate}</>}
                    </div>
                    {owner && <p className="cpp-owner-line">Owner: {owner.name} · {owner.email}</p>}
                </div>
                <div className="cpp-header-actions">
                    {!readOnly && <button className="cpp-btn-archive" onClick={() => setIsArchiveOpen(true)}>📋 Archive</button>}
                    {!readOnly && <button className="cpp-btn-edit-profile" onClick={() => setIsEditProfileOpen(true)}>✎ Edit Profile</button>}
                </div>
            </div>

            <div className="cpp-layout">
                {/* ===== LEFT COLUMN ===== */}
                <div className="cpp-main">
                    {/* Service Levels */}
                    <div className="cpp-section">
                        <div className="cpp-section-header-row">
                            <h3 className="cpp-section-title">Service Levels</h3>
                            {!readOnly && <button className="cpp-edit-link" onClick={() => setIsEditProfileOpen(true)}>Edit</button>}
                        </div>
                        <div className="cpp-field-grid-3">
                            <Field label="Company Name" value={company.name} />
                            <Field label="Account #" value={company.accountNumber} />
                            <Field label="Initial Contact Date" value={company.initialContactDate} />
                            <Field label="Business Type" value={company.businessType} />
                            <Field label="Safety Standard" value={company.safetyComplianceStandard} badge />
                            <Field label="Primary Location" value={company.primaryLocation} />
                            <Field label="NAICS Code" value={company.industryNAICS} />
                            <Field label="SIC Code" value={company.industrySIC} />
                            <Field label="Referred By" value={company.referredBy} />
                            <Field label="Assigned Rep" value={company.assignedRep} />
                            <Field label="Starter Package" value={company.starterPackage} />
                            <Field label="Status" value={status.label} statusCls={status.cls} />
                        </div>
                    </div>

                    {/* Primary Contact */}
                    <div className="cpp-section">
                        <div className="cpp-section-header-row">
                            <h3 className="cpp-section-title">Primary Contact</h3>
                            {!readOnly && <button className="cpp-edit-link" onClick={() => setIsEditProfileOpen(true)}>Edit</button>}
                        </div>
                        <div className="cpp-field-grid">
                            <Field label="Name" value={company.primaryContactName} />
                            <Field label="Position" value={company.primaryContactPosition} />
                            <Field label="Email" value={company.primaryContactEmail} isEmail />
                            <Field label="Phone" value={company.primaryContactPhone} />
                        </div>
                        <h4 style={{ fontSize: "0.75rem", fontWeight: 600, color: "var(--slate-gray)", textTransform: "uppercase", letterSpacing: "0.04em", margin: "20px 0 12px", paddingTop: "14px", borderTop: "1px solid #e8ecef" }}>
                            Secondary Contact
                        </h4>
                        <div className="cpp-field-grid">
                            <Field label="Name" value={company.secondaryContactName} />
                            <Field label="Position" value={company.secondaryContactPosition} />
                            <Field label="Email" value={company.secondaryContactEmail} isEmail />
                            <Field label="Phone" value={company.secondaryContactPhone} />
                        </div>
                    </div>

                    {/* Subscription & Module Access */}
                    <div className="cpp-section">
                        <div className="cpp-section-header-row">
                            <h3 className="cpp-section-title">Subscription & Module Access</h3>
                            {!readOnly && <button className="cpp-edit-link" onClick={() => setIsManageSubOpen(true)}>Manage</button>}
                        </div>
                        <div className="cpp-field-grid">
                            <div className="cpp-field">
                                <span className="cpp-field-label">Subscription Type</span>
                                <span className={`cpp-sub-badge ${sub.cls}`}>{sub.label}</span>
                            </div>
                            <div className="cpp-field">
                                <span className="cpp-field-label">Expiration Date</span>
                                <span className={`cpp-field-value cpp-expiration-status ${expText.cls}`}>{expText.text}</span>
                            </div>
                            <div className="cpp-field">
                                <span className="cpp-field-label">Confirmation</span>
                                <div className="cpp-confirmed-row">
                                    {owner?.cognitoId ? <><span className="cpp-confirmed-dot" /> <span className="cpp-field-value">Confirmed</span></> : <span className="cpp-field-value empty">Unconfirmed</span>}
                                </div>
                            </div>
                            <Field label="Payment Method" value={company.paymentMethod} />
                        </div>
                        <div className="cpp-module-grid-compact">
                            {MODULES.map(({ name, field }) => {
                                const isOn = field === "_always" || !!(company as Record<string, unknown>)[field];
                                return (
                                    <div key={name} className={`cpp-module-pill ${isOn ? "on" : "off"}`}>
                                        <span>{name}</span>
                                        <span className="cpp-module-pill-label">{isOn ? "ON" : "OFF"}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* ===== RIGHT COLUMN (Sidebar) ===== */}
                <div className="cpp-sidebar">
                    {/* Billing Summary */}
                    <div className="cpp-billing-card">
                        <div className="cpp-billing-header">
                            <h3 className="cpp-billing-title">Billing Summary</h3>
                            {!readOnly && <button className="cpp-edit-link" onClick={() => setIsEditBillingOpen(true)}>✎ Edit Rates</button>}
                        </div>
                        <div className="cpp-billing-total-card">
                            <p className="cpp-billing-total-label">Monthly Total</p>
                            <p className="cpp-billing-total-amount">${billing.total.toFixed(2)}</p>
                            <p className="cpp-billing-total-sub">${(billing.total * 12).toFixed(2)} / year</p>
                        </div>
                        <table className="cpp-billing-table">
                            <thead>
                                <tr><th>Module</th><th>Count</th><th>Rate</th><th>Total</th></tr>
                            </thead>
                            <tbody>
                                {billing.rows.map((row) => (
                                    <tr key={row.module}>
                                        <td>
                                            {row.module}
                                            <br />
                                            <span className={`cpp-module-status ${row.active ? "on" : "off"}`} style={{ fontSize: "0.65rem" }}>
                                                {row.active ? "CORE" : "INACTIVE"}
                                            </span>
                                        </td>
                                        <td>{row.active ? row.count : "—"}</td>
                                        <td>${row.rate.toFixed(2)}/ea</td>
                                        <td>${row.active ? (row.count * row.rate).toFixed(2) : "0"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="cpp-billing-footer">
                            <span>Monthly Total</span>
                            <span>${billing.total.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="cpp-notes-card">
                        <div className="cpp-notes-header">
                            <h3 className="cpp-notes-title">Notes</h3>
                            <button className="cpp-add-note-btn" onClick={() => setIsAddNoteOpen(true)}>+ Add Note</button>
                        </div>
                        {notes.length === 0 && <p style={{ color: "var(--slate-gray)", fontSize: "0.82rem" }}>No notes yet.</p>}
                        {notes.map((note) => {
                            const currentName = companyUser?.name || currentAdmin?.name || "";
                            const isFromOther = note.authorName !== currentName;
                            const isRecent = (Date.now() - new Date(note.createdAt!).getTime()) < 48 * 60 * 60 * 1000;
                            const isNew = isFromOther && isRecent;
                            return (
                                <div key={note.id} className={`cpp-note-item ${isNew ? "is-new" : ""}`}>
                                    <div className="cpp-note-meta">
                                        <span className="cpp-note-author">
                                            {note.authorName}
                                            {isNew && <span className="cpp-note-new-badge">New</span>}
                                        </span>
                                        <span className="cpp-note-date">{new Date(note.createdAt!).toLocaleDateString()}</span>
                                    </div>
                                    <p className="cpp-note-content">{note.content}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ===== MODALS ===== */}
            <ArchiveModal isOpen={isArchiveOpen} onClose={() => setIsArchiveOpen(false)} company={company} userCount={usage.users} equipmentCount={usage.equipment} monthlyRevenue={billing.total} onArchive={handleArchive} />
            <EditCompanyProfileModal isOpen={isEditProfileOpen} onClose={() => setIsEditProfileOpen(false)} company={company} onSave={handleSaveProfile} />
            <ManageSubscriptionModal isOpen={isManageSubOpen} onClose={() => setIsManageSubOpen(false)} company={company} onSave={handleSaveSubscription} />
            <EditBillingRatesModal isOpen={isEditBillingOpen} onClose={() => setIsEditBillingOpen(false)} billingRate={billingRate} onSave={handleSaveBillingRates} />
            <AddNoteModal isOpen={isAddNoteOpen} onClose={() => setIsAddNoteOpen(false)} onSave={handleAddNote} />
        </div>
    );
};

const Field = ({ label, value, badge, isEmail, statusCls }: { label: string; value?: string | null; badge?: boolean; isEmail?: boolean; statusCls?: string }) => (
    <div className="cpp-field">
        <span className="cpp-field-label">{label}</span>
        {badge && value ? (
            <span className="cpp-safety-badge">{value}</span>
        ) : statusCls ? (
            <span className={`cpp-status-badge ${statusCls}`}>{value}</span>
        ) : isEmail && value ? (
            <a href={`mailto:${value}`} className="cpp-field-value" style={{ color: "var(--navy-blue)", textDecoration: "underline" }}>{value}</a>
        ) : (
            <span className={`cpp-field-value ${!value ? "empty" : ""}`}>{value || "—"}</span>
        )}
    </div>
);
